import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ShipMetadata } from '../types/itinerary';

export interface NormalizedClassUpdate {
  cruiseLine: string;
  shipName: string;
  normalizedClass: string;
}

interface CruiseMetadataDoc {
  id: string;
  name: string;
  shipMetadata?: Record<string, ShipMetadata>;
}

export async function fetchCruiseMetadataDocs(): Promise<CruiseMetadataDoc[]> {
  const snapshot = await getDocs(collection(db, 'cruiseMetadata'));
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    name: docSnap.data().name as string,
    shipMetadata: (docSnap.data().shipMetadata || {}) as Record<string, ShipMetadata>,
  }));
}

/**
 * Apply normalized class updates to ship metadata across cruise lines.
 * Returns summary information to present in the UI.
 */
export async function applyNormalizedClassUpdates(
  updates: NormalizedClassUpdate[],
): Promise<{ updatedCruiseLines: number; updatedShips: number; missingCruiseLines: string[] }> {
  if (updates.length === 0) {
    return { updatedCruiseLines: 0, updatedShips: 0, missingCruiseLines: [] };
  }

  const snapshot = await getDocs(collection(db, 'cruiseMetadata'));
  const nameToDoc = new Map<string, { id: string; shipMetadata: Record<string, ShipMetadata> }>();

  snapshot.docs.forEach((docSnap) => {
    nameToDoc.set(docSnap.data().name, {
      id: docSnap.id,
      shipMetadata: (docSnap.data().shipMetadata || {}) as Record<string, ShipMetadata>,
    });
  });

  const updatesByLine = updates.reduce<Record<string, NormalizedClassUpdate[]>>((acc, update) => {
    if (!acc[update.cruiseLine]) {
      acc[update.cruiseLine] = [];
    }
    acc[update.cruiseLine].push(update);
    return acc;
  }, {});

  const missingCruiseLines: string[] = [];
  let updatedCruiseLines = 0;
  let updatedShips = 0;

  for (const [cruiseLine, lineUpdates] of Object.entries(updatesByLine)) {
    const docInfo = nameToDoc.get(cruiseLine);
    if (!docInfo) {
      missingCruiseLines.push(cruiseLine);
      continue;
    }

    const shipMetadata = { ...docInfo.shipMetadata };

    lineUpdates.forEach((update) => {
      const existingKey =
        Object.keys(shipMetadata).find((key) => key.toLowerCase() === update.shipName.toLowerCase()) ||
        update.shipName;
      const existingShipMetadata = { ...shipMetadata[existingKey] };
      
      if (!update.normalizedClass) {
        delete existingShipMetadata.normalizedClass;
      } else {
        existingShipMetadata.normalizedClass = update.normalizedClass;
      }
      
      shipMetadata[existingKey] = existingShipMetadata;
      updatedShips += 1;
    });

    await updateDoc(doc(db, 'cruiseMetadata', docInfo.id), { shipMetadata });
    updatedCruiseLines += 1;
  }

  return { updatedCruiseLines, updatedShips, missingCruiseLines };
}


